import React, { useState, useEffect, useRef } from 'react';
import { getBrand, cargarBrand } from '../utils/brand';
import PoweredByAyudaTecno from '../components/PoweredByAyudaTecno';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { platformAuthenticatorAvailable, startAuthentication } from '../utils/webauthn';
import { Fingerprint, PauseCircle, Mail, MessageCircle } from 'lucide-react';

// Marca local: este navegador ya usó / enroló biometría para el login. Sirve para
// auto-disparar el prompt al llegar (como el unlock de la Mac) solo donde tiene
// sentido — en un equipo virgen no molesta.
const BIO_HINT_KEY = 'webauthn_login_hint';

const STRINGS = {
  es: {
    welcome: 'Bienvenido',
    subtitle: 'Ingresá a {brand}',
    email: 'Correo electrónico',
    password: 'Contraseña',
    signIn: 'Ingresar',
    signingIn: 'Ingresando...',
    orWith: 'o ingresá con tu cuenta',
    twoFA: 'Verificación en dos pasos',
    twoFADesc: 'Ingresá el código de 6 dígitos de tu app de autenticación',
    twoFACode: 'Código 2FA',
    verify: 'Verificar',
    verifying: 'Verificando...',
    back: '← Volver',
    invalidCreds: 'Credenciales inválidas',
    googleError: 'Error con Google Login',
    bioSignIn: 'Entrar con biometría',
    bioFailed: 'No se pudo validar la biometría. Usá tu contraseña.',
    forgotPassword: '¿Olvidaste tu contraseña?',
    footer: '{brand} · Uso interno',
    suspTitle: 'Servicio deshabilitado',
    suspBody: 'El servicio de monitoreo y soporte está actualmente dado de baja, así que el acceso al portal quedó cerrado.',
    suspKept: 'Tu información, tus equipos y tus credenciales siguen guardados. No se borró nada: si querés retomar el servicio, lo reactivamos y volvés a entrar con este mismo usuario.',
    suspHow: 'Para volver a darlo de alta, escribime:',
    suspBack: '← Volver',
    offTitle: 'Acceso deshabilitado',
    offBody: 'Tu acceso al portal está deshabilitado en este momento.',
    offKept: 'Tu información sigue guardada. Si creés que es un error, o necesitás recuperar el acceso, escribime y lo vemos.',
    offHow: 'Escribime:',
    trialTitle: 'Tu prueba finalizó',
    trialBody: 'El período de prueba del portal terminó, así que el acceso quedó cerrado por ahora.',
    trialKept: 'Tu información, tus equipos y tus credenciales siguen guardados. No se borró nada: si querés continuar, lo activamos y volvés a entrar con este mismo usuario.',
    trialHow: 'Para continuar con el servicio, escribime:',
  },
  en: {
    welcome: 'Welcome back',
    subtitle: 'Sign in to {brand}',
    email: 'Email address',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    orWith: 'or sign in with your account',
    twoFA: 'Two-factor authentication',
    twoFADesc: 'Enter the 6-digit code from your authenticator app',
    twoFACode: '2FA Code',
    verify: 'Verify',
    verifying: 'Verifying...',
    back: '← Back',
    invalidCreds: 'Invalid credentials',
    googleError: 'Google login error',
    bioSignIn: 'Sign in with biometrics',
    bioFailed: 'Biometric validation failed. Use your password.',
    forgotPassword: 'Forgot your password?',
    footer: '{brand} · Internal use',
    suspTitle: 'Service disabled',
    suspBody: 'The monitoring and support service is currently inactive, so portal access has been closed.',
    suspKept: 'Your information, devices and credentials are still stored. Nothing was deleted: if you want the service back, we reactivate it and you sign in again with this same account.',
    suspHow: 'To reactivate it, get in touch:',
    suspBack: '← Back',
    offTitle: 'Access disabled',
    offBody: 'Your portal access is currently disabled.',
    offKept: 'Your information is still stored. If you think this is a mistake, or you need access back, get in touch and we will sort it out.',
    offHow: 'Get in touch:',
    trialTitle: 'Your trial has ended',
    trialBody: 'The portal trial period is over, so access is closed for now.',
    trialKept: 'Your information, devices and credentials are still stored. Nothing was deleted: if you want to continue, we turn it back on and you sign in again with this same account.',
    trialHow: 'To continue with the service, get in touch:',
  }
};

export default function LoginPage() {
  const [marca, setMarca] = useState(getBrand());
  // Proporción real del logo, medida al cargarlo. Un logo cuadrado y un wordmark
  // horizontal no se dibujan igual: el de Wolfsohn es 314x51 (6,2:1) y metido en una caja
  // de 48x48 quedaba de 48x8 px — ilegible. Se mide en vez de configurarlo porque cada
  // cliente trae el suyo con la forma que tenga.
  const [anchoLogo, setAnchoLogo] = useState(1);
  useEffect(() => { cargarBrand().then(setMarca); }, []);
  // Las cadenas traen {brand} y se resuelve acá, con el nombre de ESTA instancia.
  const conMarca = (t) => String(t || '').replace('{brand}', `${marca.name} ${marca.tagline}`.trim());
  const { login, setAuthData, theme } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suspended, setSuspended] = useState(null);   // { company, note } cuando el servicio está dado de baja
  const [show2FA, setShow2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'es');
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const bioAutoTried = useRef(false);

  // Ref so Google callback always has latest values without re-initializing the button
  const authRef = useRef({ setAuthData, navigate });
  useEffect(() => { authRef.current = { setAuthData, navigate }; }, [setAuthData, navigate]);

  // Vuelta del puente entre instancias: el destino ya validó el pase y nos manda la
  // sesión lista. Es el mismo formato que el callback de Google — ver auth.js bridge-login.
  useEffect(() => {
    const bridgeAuth = searchParams.get('bridge_auth');
    const bridgeError = searchParams.get('bridge_error');
    if (bridgeAuth) {
      try {
        setAuthData(JSON.parse(decodeURIComponent(bridgeAuth)));
        navigate('/', { replace: true });
      } catch {
        setError('No se pudo completar el acceso');
      }
    } else if (bridgeError) {
      setError({
        off: 'El acceso directo no está habilitado en esta instancia',
        invalid: 'El acceso venció o ya fue usado. Probá de nuevo desde el helpdesk.',
        no_user: 'No tenés una cuenta activa en esta instancia. Pedile a un admin que te dé de alta.',
        not_internal: 'Esta entrada es para usuarios internos',
        totp: 'Tu cuenta acá tiene 2FA: entrá con usuario y contraseña',
      }[bridgeError] || 'No se pudo completar el acceso');
    }
  }, [searchParams, setAuthData, navigate]);

  // Handle Google redirect callback
  useEffect(() => {
    const googleAuth = searchParams.get('google_auth');
    const googleError = searchParams.get('google_error');
    if (googleAuth) {
      try {
        const data = JSON.parse(decodeURIComponent(googleAuth));
        setAuthData(data);
        navigate('/', { replace: true });
      } catch (err) {
        setError('Error procesando login de Google');
      }
    } else if (googleError) {
      const errorMessages = {
        domain: 'Solo cuentas @ayudatecno.com.ar',
        not_found: 'Usuario no encontrado. Contactá a Andy.',
        failed: 'Error con Google Login',
        no_credential: 'Error con Google Login',
      };
      setError(errorMessages[googleError] || 'Error con Google Login');
    }
  }, [searchParams, setAuthData, navigate]);

  const s = STRINGS[lang];

  const toggleLang = (l) => {
    setLang(l);
    localStorage.setItem('lang', l);
  };

  useEffect(() => {
    const initGoogle = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: '21049100477-nc6oec98jongfh0eodafigumcj62aro4.apps.googleusercontent.com',
        callback: async (response) => {
          setError('');
          try {
            const res = await api.post('/auth/google', { credential: response.credential });
            authRef.current.setAuthData(res.data);
            authRef.current.navigate('/');
          } catch (err) {
            setError(err.response?.data?.error || 'Error con Google Login');
          }
        },
        ux_mode: 'redirect',
        login_uri: window.location.origin + '/api/auth/google/callback',
        hosted_domain: 'ayudatecno.com.ar',
      });
      const btn = document.getElementById('google-signin-btn');
      if (btn) {
        window.google.accounts.id.renderButton(btn, {
          theme: document.documentElement.getAttribute('data-theme') === 'light' ? 'outline' : 'filled_black',
          size: 'large', width: 320, shape: 'rectangular',
          type: 'standard',
          text: 'signin_with',
        });
      }
    };

    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      if (window.google) initGoogle();
      else existing.addEventListener('load', initGoogle);
    } else {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    }
  }, []);

  // Re-render Google button when theme changes
  useEffect(() => {
    if (!window.google) return;
    const btn = document.getElementById('google-signin-btn');
    if (btn) {
      btn.innerHTML = '';
      window.google.accounts.id.renderButton(btn, {
        theme: theme === 'light' ? 'outline' : 'filled_black',
        size: 'large', width: 320, shape: 'rectangular'
      });
    }
  }, [theme]);

  const handleBiometricLogin = React.useCallback(async () => {
    setError('');
    setBioLoading(true);
    try {
      const opt = await api.post('/auth/webauthn/login-options');
      const assertion = await startAuthentication(opt.data.options);
      const res = await api.post('/auth/webauthn/login-verify', {
        response: assertion,
        challengeToken: opt.data.challengeToken,
      });
      localStorage.setItem(BIO_HINT_KEY, '1');
      authRef.current.setAuthData(res.data);
      authRef.current.navigate('/');
    } catch (err) {
      // NotAllowedError = canceló el prompt del sistema (o no hay passkey acá) — sin ruido.
      if (err?.name !== 'NotAllowedError') {
        setError(err.response?.data?.error || STRINGS[localStorage.getItem('lang') || 'es'].bioFailed);
      }
    } finally {
      setBioLoading(false);
    }
  }, []);

  // Disponibilidad + auto-disparo: si este navegador ya usó biometría para entrar,
  // el prompt salta solo al llegar al login (una vez). Si no, queda el botón.
  useEffect(() => {
    platformAuthenticatorAvailable().then(ok => {
      setBioAvailable(ok);
      if (ok && localStorage.getItem(BIO_HINT_KEY) === '1' && !bioAutoTried.current) {
        bioAutoTried.current = true;
        handleBiometricLogin();
      }
    });
  }, [handleBiometricLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, show2FA ? totpCode : undefined);
      if (result?.requires2FA) { setShow2FA(true); setLoading(false); return; }
      navigate('/');
    } catch (err) {
      // Servicio dado de baja: no es un error de credenciales, así que no va en
      // el cuadro rojo. El cliente tiene que entender que sus datos están y que
      // hay un camino de vuelta — si sólo ve "credenciales inválidas" asume que
      // se rompió algo y nos escribe por el canal equivocado, o no escribe.
      const code = err.response?.data?.code;
      if (code === 'SERVICE_SUSPENDED' || code === 'ACCESS_DISABLED' || code === 'PORTAL_TRIAL_ENDED') {
        setSuspended({ kind: code, company: err.response.data.company || null, note: err.response.data.note || null });
        setPassword('');
      } else {
        setError(err.response?.data?.error || s.invalidCreds);
      }
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de servicio dado de baja. Reemplaza el formulario entero en vez de
  // aparecer arriba de él: dejarle el campo de contraseña a la vista invita a
  // seguir probando una clave que no está mal.
  if (suspended) {
    // Dos situaciones distintas con la misma pantalla: la empresa entera dada de
    // baja (trial cerrado) y una persona sin acceso en una empresa que sigue
    // activa. Mezclarlas seria mentirle a una de las dos: al empleado no le
    // sirve "contratá el servicio", y al cliente no le sirve "pedí tu acceso".
    // Tres situaciones distintas con la misma pantalla: la prueba que se venció, la
    // empresa entera dada de baja, y una persona sin acceso en una empresa que sigue
    // activa. Mezclarlas seria mentirle a alguna: al empleado no le sirve "contratá el
    // servicio", al del trial no le sirve "tu servicio fue dado de baja" (nunca lo
    // tuvo), y al cliente no le sirve "pedí tu acceso".
    const esEmpresa = suspended.kind !== 'ACCESS_DISABLED';
    const txt = suspended.kind === 'PORTAL_TRIAL_ENDED'
      ? { title: s.trialTitle, body: s.trialBody, kept: s.trialKept, how: s.trialHow }
      : esEmpresa
        ? { title: s.suspTitle, body: s.suspBody, kept: s.suspKept, how: s.suspHow }
        : { title: s.offTitle,  body: s.offBody,  kept: s.offKept,  how: s.offHow  };
    return (
      <div className="login-page">
        <div className="login-card">
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <PauseCircle size={40} style={{ color: 'var(--c-pending, #f59e0b)' }} />
            <h1 style={{ fontSize: 20, margin: '12px 0 4px', color: 'var(--c-text)' }}>{txt.title}</h1>
            {esEmpresa && suspended.company && (
              <div style={{ fontSize: 14, color: 'var(--c-text-muted)' }}>{suspended.company}</div>
            )}
          </div>

          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--c-text)', marginBottom: 12 }}>{txt.body}</p>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--c-text-muted)', marginBottom: 18 }}>{txt.kept}</p>

          {suspended.note && (
            <div style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: 'var(--c-text)', marginBottom: 18 }}>
              {suspended.note}
            </div>
          )}

          <div style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)', marginBottom: 10 }}>{txt.how}</div>
            <a href="mailto:andy@ayudatecno.com.ar" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--c-brand)', textDecoration: 'none', marginBottom: 8 }}>
              <Mail size={16} /> andy@ayudatecno.com.ar
            </a>
            <a href="https://wa.me/5491157587882" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--c-brand)', textDecoration: 'none' }}>
              <MessageCircle size={16} /> Directo Andy (+54 9 11) 5758 7882
            </a>
          </div>

          <button className="btn btn-secondary w-full" onClick={() => { setSuspended(null); setError(''); }}>
            {s.suspBack}
          </button>

          <div style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: 'var(--c-text-muted)' }}>{conMarca(s.footer)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">

        {/* Language toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 4 }}>
          {['es', 'en'].map(l => (
            <button key={l} onClick={() => toggleLang(l)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px',
              fontSize: 11, borderRadius: 4, fontWeight: lang === l ? 700 : 400,
              color: lang === l ? 'var(--c-brand)' : 'var(--c-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              {l === 'es' ? '🇦🇷 ES' : '🇺🇸 EN'}
            </button>
          ))}
        </div>

        {/* Logo */}
        <div className="login-logo">
          <img
            src={marca.logoUrl || '/logo.jpg'}
            alt={marca.name}
            onLoad={(e) => setAnchoLogo((e.target.naturalWidth || 1) / (e.target.naturalHeight || 1))}
            style={{
              // Alto fijo y ancho libre: así el logo conserva SU forma. Un cuadrado ocupa
              // 48x48 como antes; un wordmark horizontal se estira hasta poder leerse.
              height: 44, width: 'auto', maxWidth: 250, objectFit: 'contain',
              borderRadius: anchoLogo > 2.5 ? 0 : 10,
            }}
          />
          {/* Un wordmark ancho YA dice el nombre: repetirlo al lado es ruido y encima le
              roba el espacio que necesita para leerse. Con un logo cuadrado (un isotipo),
              el nombre sí hace falta. */}
          {anchoLogo <= 2.5 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.5px' }}>{marca.name}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{marca.tagline}</div>
            </div>
          )}
        </div>

        <div className="login-title">{s.welcome}</div>
        <div className="login-subtitle" style={{ marginBottom: 28 }}>{conMarca(s.subtitle)}</div>

        {!show2FA ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div id="google-signin-btn"></div>
            </div>

            {bioAvailable && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <button type="button" className="btn btn-secondary" style={{ width: 320 }}
                  onClick={handleBiometricLogin} disabled={bioLoading}>
                  {bioLoading
                    ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    : <><Fingerprint size={16} /> {s.bioSignIn}</>}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, color: 'var(--c-text-subtle)', fontSize: 12 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
              {s.orWith}
              <div style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
            </div>

            <form onSubmit={handleSubmit} method="post">
              <div className="form-group">
                <label className="form-label" htmlFor="email">{s.email}</label>
                <input id="email" name="email" type="email" className="form-input"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="vos@ayudatecno.com.ar" autoComplete="username" required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="password">{s.password}</label>
                <input id="password" name="password" type="password" className="form-input"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" autoComplete="current-password" required />
              </div>

              {error && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: '#ef4444', marginBottom: 16 }}>
                  {error}
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-lg w-full" disabled={loading} style={{ marginTop: 8 }}>
                {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> {s.signingIn}</> : s.signIn}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--c-text-muted)', textDecoration: 'none' }}>
                {s.forgotPassword}
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} method="post">
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{s.twoFA}</div>
              <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{s.twoFADesc}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{s.twoFACode}</label>
              <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                className="form-input" value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" autoComplete="one-time-code" autoFocus
                style={{ textAlign: 'center', letterSpacing: 8, fontSize: 24 }} />
            </div>
            {error && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: '#ef4444', marginBottom: 16 }}>
                {error}
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-lg w-full" disabled={loading || totpCode.length !== 6}>
              {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> {s.verifying}</> : s.verify}
            </button>
            <button type="button" className="btn btn-ghost w-full" style={{ marginTop: 8 }}
              onClick={() => { setShow2FA(false); setTotpCode(''); setError(''); }}>
              {s.back}
            </button>
          </form>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: 'var(--c-text-subtle)' }}>
          {conMarca(s.footer)}
          <div style={{ textAlign: 'center', marginTop: 10 }}><PoweredByAyudaTecno /></div>
        </div>
      </div>
    </div>
  );
}
